import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  Save, Building2, Clock, History, Search, UserCog, Gauge, Fingerprint,
  KeyRound, Lock, Trash2, UserPlus, ShieldCheck, Eye,
} from "lucide-react";
import { registerPasskey, passkeysSupported } from "@/lib/passkeys";

function apiError(e, fallback = "Operazione non riuscita") {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
}

export default function Settings() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [form, setForm] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  useEffect(() => { if (data) setForm(data); }, [data]);

  const save = useMutation({
    mutationFn: async () => (await api.put("/settings", form)).data,
    onSuccess: () => {
      toast.success("Impostazioni salvate");
      qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: () => toast.error("Errore durante il salvataggio"),
  });

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  if (isLoading || !form) {
    return <div className="max-w-3xl space-y-4"><Skeleton className="h-64 w-full" /><Skeleton className="h-48 w-full" /></div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="max-w-3xl space-y-6" data-testid="settings-page">
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Building2 className="h-5 w-5 text-blue-700" /> Azienda
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Nome azienda</Label>
              <Input className="mt-1" data-testid="company-name-input" value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} />
            </div>
            <div>
              <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><Clock className="h-3 w-3" /> Fuso orario</Label>
              <Input className="mt-1" data-testid="timezone-input" value={form.timezone || ""} onChange={(e) => set("timezone", e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Logo</Label>
            <div className="mt-1 flex items-center gap-4">
              {form.logo ? (
                <img src={form.logo} alt="logo" className="h-14 w-14 rounded-md border border-border object-contain" />
              ) : (
                <div className="flex h-14 w-14 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
                  <Building2 className="h-5 w-5" />
                </div>
              )}
              <Input type="file" accept="image/*" onChange={onLogo} data-testid="logo-input" className="max-w-xs" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <History className="h-5 w-5 text-blue-700" /> Importazione Gmail
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intervallo storico da importare (giorni)</Label>
            <Input type="number" min={0} className="mt-1 max-w-[200px]" data-testid="history-days-input"
              value={form.history_days ?? 30} onChange={(e) => set("history_days", parseInt(e.target.value || "0", 10))} />
            <p className="mt-1 text-xs text-muted-foreground">0 = importa tutte le email disponibili senza limite di data.</p>
          </div>
          <div>
            <Label className="flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"><Search className="h-3 w-3" /> Query Gmail monitorata</Label>
            <Input className="mt-1 font-mono text-sm" data-testid="gmail-query-input" value={form.gmail_query || ""} onChange={(e) => set("gmail_query", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Clock className="h-5 w-5 text-blue-700" /> Sincronizzazione Pipedrive
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Intervallo di sincronizzazione (minuti)</Label>
            <Input type="number" min={1} className="mt-1 max-w-[200px]" data-testid="pipedrive-interval-input"
              value={form.pipedrive_sync_interval_minutes ?? 10}
              onChange={(e) => set("pipedrive_sync_interval_minutes", parseInt(e.target.value || "0", 10))} />
            <p className="mt-1 text-xs text-muted-foreground">
              Ogni quanti minuti l'app controlla le modifiche su Pipedrive. Valori più alti riducono il consumo del budget API (consigliato 10).
              I cicli senza modifiche vengono saltati automaticamente e, in caso di limite raggiunto, la sync va in pausa e riprende da sola.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Storico per-contatto (giorni)</Label>
              <Input type="number" min={7} className="mt-1 max-w-[200px]" data-testid="changelog-days-input"
                value={form.changelog_history_days ?? 120}
                onChange={(e) => set("changelog_history_days", parseInt(e.target.value || "0", 10))} />
              <p className="mt-1 text-xs text-muted-foreground">
                Lo storico proprietario/etichette (1 chiamata API per contatto, il costo maggiore) viene scaricato solo per i lead ricevuti negli ultimi N giorni. Consigliato 120.
              </p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tetto giornaliero token API</Label>
              <Input type="number" min={1000} step={1000} className="mt-1 max-w-[200px]" data-testid="pipedrive-budget-input"
                value={form.pipedrive_daily_budget ?? 40000}
                onChange={(e) => set("pipedrive_daily_budget", parseInt(e.target.value || "0", 10))} />
              <p className="mt-1 text-xs text-muted-foreground">
                Il budget Pipedrive è a <strong>token</strong> (~120.000/giorno per l'intero account, condiviso). La sync si ferma al raggiungimento del tetto e riprende il giorno dopo, lasciando margine al tuo lavoro manuale. Consigliato 40.000.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="pr-4">
              <p className="text-sm font-medium">Metti in pausa la sincronizzazione</p>
              <p className="text-xs text-muted-foreground">
                Utile in anteprima: anteprima e app pubblicata usano lo stesso budget Pipedrive. Tienila attiva solo dove serve.
              </p>
            </div>
            <Switch data-testid="pipedrive-pause-switch"
              checked={!!form.pipedrive_sync_paused}
              onCheckedChange={(v) => set("pipedrive_sync_paused", v)} />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-4">
            <div className="pr-4">
              <p className="text-sm font-medium">Scarica lo storico dettagliato dei contatti</p>
              <p className="text-xs text-muted-foreground">
                Ricostruisce lo storico di proprietario/etichette (1 chiamata API <strong>per contatto</strong>): molto costoso in termini di budget Pipedrive. Lascialo <strong>disattivato</strong>: velocità, conversioni, vendite ed etichetta attuale funzionano comunque con i dati scaricati in blocco.
              </p>
            </div>
            <Switch data-testid="changelog-history-switch"
              checked={!!form.fetch_changelog_history}
              onCheckedChange={(v) => set("fetch_changelog_history", v)} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
            <Gauge className="h-5 w-5 text-blue-700" /> Soglie analisi commerciale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Tempo massimo di contatto (minuti)</Label>
            <Input type="number" min={1} className="mt-1 max-w-[200px]" data-testid="max-contact-input"
              value={form.max_contact_minutes ?? 60} onChange={(e) => set("max_contact_minutes", parseInt(e.target.value || "0", 10))} />
            <p className="mt-1 text-xs text-muted-foreground">Entro questa soglia il lead è considerato contattato nei tempi (verde).</p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Potenz. dormiente (giorni)</Label>
              <Input type="number" min={1} className="mt-1" data-testid="dormant-potential-input"
                value={form.dormant_potential_days ?? 8} onChange={(e) => set("dormant_potential_days", parseInt(e.target.value || "0", 10))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Dormiente (giorni)</Label>
              <Input type="number" min={1} className="mt-1" data-testid="dormant-input"
                value={form.dormant_days ?? 31} onChange={(e) => set("dormant_days", parseInt(e.target.value || "0", 10))} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Dormiente lungo periodo (giorni)</Label>
              <Input type="number" min={1} className="mt-1" data-testid="dormant-long-input"
                value={form.dormant_long_days ?? 60} onChange={(e) => set("dormant_long_days", parseInt(e.target.value || "0", 10))} />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button className="gap-2" data-testid="save-settings-button" onClick={() => save.mutate()} disabled={save.isPending}>
          <Save className="h-4 w-4" /> Salva impostazioni
        </Button>
      </div>

      <div className="pt-2">
        <h2 className="mb-1 font-display text-lg font-bold tracking-tight">Sicurezza e accessi</h2>
        <p className="mb-4 text-sm text-muted-foreground">Il tuo account: <strong>{user?.email}</strong>
          <span className="ml-2 inline-block rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700">{isAdmin ? "Admin" : "Consultazione"}</span>
        </p>
      </div>

      <CredentialsCard />
      <PasskeyCard />
      {isAdmin && <ViewerAccountsCard />}
    </div>
  );
}

function CredentialsCard() {
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");

  const changePwd = useMutation({
    mutationFn: async () => (await api.post("/auth/change-password", { current_password: cur, new_password: np })).data,
    onSuccess: () => { toast.success("Password aggiornata"); setCur(""); setNp(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const changePin = useMutation({
    mutationFn: async () => (await api.post("/auth/change-pin", { current_pin: curPin, new_pin: newPin })).data,
    onSuccess: () => { toast.success("PIN aggiornato"); setCurPin(""); setNewPin(""); },
    onError: (e) => toast.error(apiError(e)),
  });
  const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 4);

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <KeyRound className="h-5 w-5 text-blue-700" /> Password e PIN
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cambia password</p>
          <Input type="password" placeholder="Password attuale" data-testid="cur-password-input" value={cur} onChange={(e) => setCur(e.target.value)} />
          <Input type="password" placeholder="Nuova password (min 8)" data-testid="new-password-input" value={np} onChange={(e) => setNp(e.target.value)} />
          <Button size="sm" className="gap-2" data-testid="save-password-button" disabled={changePwd.isPending || !cur || np.length < 8} onClick={() => changePwd.mutate()}>
            <Lock className="h-4 w-4" /> Aggiorna password
          </Button>
        </div>
        <div className="space-y-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cambia PIN</p>
          <Input type="password" inputMode="numeric" maxLength={4} placeholder="PIN attuale" data-testid="cur-pin-input" value={curPin} onChange={(e) => setCurPin(onlyDigits(e.target.value))} />
          <Input type="password" inputMode="numeric" maxLength={4} placeholder="Nuovo PIN (4 cifre)" data-testid="new-pin-input" value={newPin} onChange={(e) => setNewPin(onlyDigits(e.target.value))} />
          <Button size="sm" className="gap-2" data-testid="save-pin-button" disabled={changePin.isPending || newPin.length !== 4} onClick={() => changePin.mutate()}>
            <ShieldCheck className="h-4 w-4" /> Aggiorna PIN
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PasskeyCard() {
  const qc = useQueryClient();
  const supported = passkeysSupported();
  const { data: keys, isLoading } = useQuery({
    queryKey: ["passkeys"],
    queryFn: async () => (await api.get("/auth/passkey/list")).data,
  });
  const [busy, setBusy] = useState(false);

  const doRegister = async () => {
    setBusy(true);
    try {
      await registerPasskey();
      toast.success("Dispositivo registrato: ora puoi accedere con l'impronta");
      qc.invalidateQueries({ queryKey: ["passkeys"] });
    } catch (e) {
      toast.error(apiError(e, "Registrazione non riuscita"));
    } finally { setBusy(false); }
  };
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/auth/passkey/${encodeURIComponent(id)}`)).data,
    onSuccess: () => { toast.success("Dispositivo rimosso"); qc.invalidateQueries({ queryKey: ["passkeys"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Fingerprint className="h-5 w-5 text-blue-700" /> Accesso con impronta / Face ID
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Registra questo dispositivo per accedere rapidamente con impronta, Face ID o Windows Hello.
          I dati biometrici restano sul dispositivo e non vengono mai inviati al server.
        </p>
        {!supported && (
          <p className="text-sm font-medium text-amber-600">Questo browser/dispositivo non supporta l'accesso biometrico.</p>
        )}
        <Button size="sm" className="gap-2" data-testid="register-passkey-button" disabled={!supported || busy} onClick={doRegister}>
          <Fingerprint className="h-4 w-4" /> Registra questo dispositivo
        </Button>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : keys?.length ? (
            keys.map((k) => (
              <div key={k.id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid="passkey-row">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{k.label || "Dispositivo"}</p>
                  <p className="text-xs text-muted-foreground">Registrato il {new Date(k.created_at).toLocaleDateString("it-IT")}</p>
                </div>
                <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" data-testid="delete-passkey-button" onClick={() => del.mutate(k.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Nessun dispositivo registrato.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ViewerAccountsCard() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const { data: users, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });

  const create = useMutation({
    mutationFn: async () => (await api.post("/users", { email, password, pin })).data,
    onSuccess: () => {
      toast.success("Account di consultazione creato");
      setEmail(""); setPassword(""); setPin("");
      qc.invalidateQueries({ queryKey: ["users"] });
    },
    onError: (e) => toast.error(apiError(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/users/${id}`)).data,
    onSuccess: () => { toast.success("Account eliminato"); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const onlyDigits = (v) => v.replace(/\D/g, "").slice(0, 4);
  const viewers = (users || []).filter((u) => u.role === "viewer");

  return (
    <Card className="shadow-sm">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 font-display text-base tracking-tight">
          <Eye className="h-5 w-5 text-blue-700" /> Account solo consultazione
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Gli account di consultazione possono <strong>solo visualizzare</strong> i dati: nessuna modifica o azione è consentita.
          Email, password e PIN a 4 cifre sono obbligatori.
        </p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input type="email" placeholder="Email" data-testid="viewer-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Input type="text" placeholder="Password (min 8)" data-testid="viewer-password-input" value={password} onChange={(e) => setPassword(e.target.value)} />
          <Input type="text" inputMode="numeric" maxLength={4} placeholder="PIN (4 cifre)" data-testid="viewer-pin-input" value={pin} onChange={(e) => setPin(onlyDigits(e.target.value))} />
        </div>
        <Button size="sm" className="gap-2" data-testid="create-viewer-button"
          disabled={create.isPending || !email || password.length < 8 || pin.length !== 4}
          onClick={() => create.mutate()}>
          <UserPlus className="h-4 w-4" /> Crea account
        </Button>

        <div className="space-y-2">
          {isLoading ? (
            <Skeleton className="h-10 w-full" />
          ) : viewers.length ? (
            viewers.map((u) => (
              <div key={u.user_id} className="flex items-center justify-between rounded-lg border border-border p-3" data-testid="viewer-row">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">{u.email}</p>
                  <p className="text-xs text-muted-foreground">Sola consultazione</p>
                </div>
                <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700" data-testid="delete-viewer-button" onClick={() => del.mutate(u.user_id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground">Nessun account di consultazione.</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
